import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addErrorNotification, addSelectedSite, getFilmById, getShowtimeById, getSiteById, hasMaximumSelectedSites, isSiteSelected, NotificationType, removeFirstSelectedSite } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useImperativeHandle, useReducer } from 'react';
import { useMemberRewards } from '../../hooks/use-member-rewards';
import { Button } from '../../subcomponents/button/button';
import { ButtonColor } from '../../subcomponents/button/types/color';
import { Heading, HeadingLevel } from '../../subcomponents/heading/heading';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { QuickNavigatorFilmDropdown } from './subcomponents/film-dropdown';
import { QuickNavigatorScreeningDatesDropdown } from './subcomponents/screening-date-dropdown';
import { QuickNavigatorShowtimeDropdown } from './subcomponents/showtime-dropdown';
import { QuickNavigatorSiteDropdown } from './subcomponents/site-dropdown';
import { mapQuickNavigatorContext, QuickNavigatorContextInstance } from './types/context';
import { defaultQuickNavigatorProps, quickNavigatorPropsValidator } from './types/props';
import { defaultQuickNavigatorState } from './types/state';
import { quickNavigatorActionReducer } from './utils/action-reducer';
import { QuickNavigatorActionType } from './utils/action-type';
const b = bemClassBuilder('v-quick-navigator');
export const QuickNavigatorBase = (props, ref) => {
    const { config, displayText, events } = props;
    const { headingLabel } = config;
    // Load member rewards to show reward-restricted booking periods.
    useMemberRewards();
    const [state, dispatch] = useReducer(quickNavigatorActionReducer, Object.assign({}, defaultQuickNavigatorState));
    const context = mapQuickNavigatorContext(props, state, dispatch);
    const { dropdownSelections } = state;
    const handleResetButtonClicked = useCallback(() => {
        var _a;
        dispatch({ type: QuickNavigatorActionType.ResetDropdownSelections });
        (_a = events.onResetButtonClicked) === null || _a === void 0 ? void 0 : _a.call(events);
    }, [dispatch, events]);
    const handleActionButtonClicked = useCallback(() => {
        var _a;
        if (!dropdownSelections.site && !dropdownSelections.film) {
            addErrorNotification({
                id: getIdForComponentNotification('quick-navigator', NotificationType.Error, 'no-site-or-film-selected'),
                messages: [displayText.noSiteOrFilmSelectedErrorMessage]
            });
            return;
        }
        (_a = events.onActionButtonClicked) === null || _a === void 0 ? void 0 : _a.call(events, getDropdownSelections(dropdownSelections));
    }, [displayText.noSiteOrFilmSelectedErrorMessage, dropdownSelections, events]);
    const handleAddSelectedSiteToStore = useCallback(() => {
        const siteId = dropdownSelections.site;
        if (!siteId)
            return;
        const siteAlreadySelected = isSiteSelected(siteId);
        if (siteAlreadySelected)
            return;
        const maxSitesSelected = hasMaximumSelectedSites();
        if (maxSitesSelected)
            removeFirstSelectedSite();
        addSelectedSite(siteId);
    }, [dropdownSelections.site]);
    useImperativeHandle(ref, () => ({
        addSelectedSiteToStore: handleAddSelectedSiteToStore
    }));
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsxs(QuickNavigatorContextInstance.Provider, Object.assign({ value: context }, { children: [headingLabel.show && (_jsx(Heading, Object.assign({ className: b.element('heading'), level: HeadingLevel.H3 }, { children: displayText.headingLabel }))), _jsx(QuickNavigatorSiteDropdown, {}), _jsx(QuickNavigatorFilmDropdown, {}), _jsx(QuickNavigatorScreeningDatesDropdown, {}), _jsx(QuickNavigatorShowtimeDropdown, {}), _jsxs("div", Object.assign({ className: b.element('button-wrapper') }, { children: [_jsx(Button, { className: b.element('action-button'), label: displayText.actionButtonLabel, color: ButtonColor.Primary, onClick: handleActionButtonClicked }), _jsx(Button, { className: b.element('reset-button'), label: displayText.resetButtonLabel, color: ButtonColor.Tertiary, onClick: handleResetButtonClicked })] }))] })) })));
};
export const QuickNavigator = observer(forwardRef(QuickNavigatorBase));
QuickNavigator.displayName = 'QuickNavigator';
/**
 * The **Quick Navigator** component allows a user to quickly navigate to different pages in the booking flow by
 * selecting from four dropdowns: Site, Film, Screening Date and Showtime.
 */
export const QuickNavigatorComponent = withComponentWrapper(QuickNavigator, defaultQuickNavigatorProps, quickNavigatorPropsValidator);
const getDropdownSelections = (selections) => {
    const { site: siteId, film: filmId, screeningDate: screeningDate, showtime: showtimeId } = selections;
    return {
        site: siteId ? getSiteById(siteId) : undefined,
        film: filmId ? getFilmById(filmId) : undefined,
        showtime: showtimeId ? getShowtimeById(showtimeId) : undefined,
        screeningDate
    };
};
